import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * Hide mode: masks figures and contact details across the dashboard, for
 * screen-sharing, demoing, and the landing-page captures.
 *
 * State is per page rather than one global flag, so an operator can show the
 * revenue page to a visitor while the driver list stays masked.
 *
 * Masking is presentational. The API still returns real values and the browser
 * still holds them in memory — this defeats a shoulder-surfer or a screenshot,
 * not devtools.
 */

/** The pages that own a hide setting. */
export const PRIVACY_SCOPES = [
  'metrics',
  'sessions',
  'stations',
  'users',
  'transactions',
  'stationDetails',
] as const;

export type PrivacyScope = (typeof PRIVACY_SCOPES)[number];

export const PRIVACY_STORAGE_KEY = 'dashboardPrivacy';

type PrivacyState = Partial<Record<PrivacyScope, boolean>>;

interface PrivacyContextValue {
  state: PrivacyState;
  setScope: (scope: PrivacyScope, hidden: boolean) => void;
}

const PrivacyContext = createContext<PrivacyContextValue | null>(null);

const isScope = (key: string): key is PrivacyScope =>
  (PRIVACY_SCOPES as readonly string[]).includes(key);

/**
 * Read the stored settings, keeping only keys that are still pages.
 *
 * Anything unreadable — absent, malformed, hand-edited, or a storage that
 * throws — falls back to everything visible. Hide mode is a convenience; it
 * must never be the reason the dashboard fails to render.
 */
function readStored(): PrivacyState {
  try {
    const raw = localStorage.getItem(PRIVACY_STORAGE_KEY);
    if (!raw) return {};

    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return {};

    const state: PrivacyState = {};
    Object.entries(parsed as Record<string, unknown>).forEach(([key, value]) => {
      if (isScope(key) && typeof value === 'boolean') state[key] = value;
    });
    return state;
  } catch {
    return {};
  }
}

function writeStored(state: PrivacyState): void {
  try {
    localStorage.setItem(PRIVACY_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Private browsing and full quotas throw here. The in-memory state still
    // applies for this session; only persistence is lost.
  }
}

export const PrivacyProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<PrivacyState>(readStored);

  const setScope = useCallback(
    (scope: PrivacyScope, hidden: boolean) =>
      setState((prev) => {
        const next = { ...prev, [scope]: hidden };
        writeStored(next);
        return next;
      }),
    [],
  );

  const value = useMemo(() => ({ state, setScope }), [state, setScope]);

  return <PrivacyContext.Provider value={value}>{children}</PrivacyContext.Provider>;
};

function usePrivacyContext(): PrivacyContextValue {
  const context = useContext(PrivacyContext);
  if (!context) {
    throw new Error('usePrivacy must be used inside a PrivacyProvider');
  }
  return context;
}

/** Hide state for one page. */
export function usePrivacy(scope: PrivacyScope) {
  const { state, setScope } = usePrivacyContext();
  const hidden = state[scope] ?? false;

  const toggle = useCallback(() => setScope(scope, !hidden), [setScope, scope, hidden]);

  return { hidden, toggle };
}
